import React, { useCallback, useEffect, useRef, useState } from "react";
import { EVENT_NAMES } from "@scrile/api-provider/dist";
import { AiFeatureDisabledError } from "lib/aiApi";
import emitter from "lib/emitter";
import { getBcp47Locale } from "lib/dateLocale";
import { useTranslation } from "locales";
import {
  deleteVoiceProfile,
  enrollVoice,
  fetchVoiceCloneStatus,
  VoiceCloneStatus,
} from "lib/voiceCloneApi";
import "./style.scss";

interface Props {
  className?: string;
}

// Two phonetically diverse sentences (~7-10 s of speech) — kept short
// on purpose. F5-TTS estimates output duration by the ratio of
// gen_text to ref_text length, so a long enrollment script
// underbudgets short translations and yields ~0.5 s of mumbling.
// 5-10 s of reference is the sweet spot for the model.
const SCRIPTS: Record<string, { label: string; sentences: string[] }> = {
  en: {
    label: "English",
    sentences: [
      "Hi, my name is here, and I work with this team every day.",
      "Today the weather is calm and the sky is clear blue.",
    ],
  },
  ru: {
    label: "Русский",
    sentences: [
      "Привет, меня зовут так-то, и я работаю с этой командой каждый день.",
      "Сегодня погода спокойная, и небо ясное голубое.",
    ],
  },
};

const MAX_RECORDING_SEC = 60;
const MIN_RECORDING_SEC = 5;

type StateMode = "idle" | "recording" | "review" | "uploading" | "done";

const VoiceEnrollment: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const [scriptLang, setScriptLang] = useState<keyof typeof SCRIPTS>("en");
  const [status, setStatus] = useState<VoiceCloneStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [mode, setMode] = useState<StateMode>("idle");
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const tickerRef = useRef<number | null>(null);

  const reloadStatus = useCallback(async () => {
    setStatusLoading(true);
    setError(null);
    try {
      const r = await fetchVoiceCloneStatus();
      setStatus(r);
    } catch (e) {
      if (e instanceof AiFeatureDisabledError) setDisabled(true);
      else setError(e instanceof Error ? e.message : String(e));
    } finally {
      setStatusLoading(false);
    }
  }, []);

  // Initial status fetch needs the auth token, which the SDK populates
  // asynchronously over the websocket handshake. On a fresh page load
  // this hook can run before SET_USER has fired — voiceCloneStatus then
  // throws "Authentication required". We try immediately (covers
  // navigation within an already-authed app) and re-fetch on every
  // SET_USER event so the panel refreshes once auth is ready.
  useEffect(() => {
    reloadStatus();
    emitter.on(EVENT_NAMES.SET_USER, reloadStatus);
    return () => emitter.off(EVENT_NAMES.SET_USER, reloadStatus);
  }, [reloadStatus]);

  // Cleanup recorder + object URL on unmount
  useEffect(() => () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* */ }
    }
    if (tickerRef.current) window.clearInterval(tickerRef.current);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  const startRecording = useCallback(async () => {
    setError(null);
    if (audioUrl) { URL.revokeObjectURL(audioUrl); setAudioUrl(null); }
    setAudioBlob(null);
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = rec;

      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        try {
          const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
          setAudioBlob(blob);
          setAudioUrl(URL.createObjectURL(blob));
          setMode("review");
        } finally {
          stream.getTracks().forEach((t) => t.stop());
          if (tickerRef.current) { window.clearInterval(tickerRef.current); tickerRef.current = null; }
        }
      };
      rec.start();
      startedAtRef.current = Date.now();
      setElapsedSec(0);
      setMode("recording");
      tickerRef.current = window.setInterval(() => {
        const sec = (Date.now() - startedAtRef.current) / 1000;
        setElapsedSec(sec);
        if (sec >= MAX_RECORDING_SEC && rec.state !== "inactive") {
          rec.stop();
        }
      }, 200);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Microphone access denied");
      setMode("idle");
    }
  }, [audioUrl]);

  const stopRecording = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!audioBlob) return;
    if (!consent) {
      setError("Please confirm consent before submitting your voice profile.");
      return;
    }
    if (elapsedSec < MIN_RECORDING_SEC) {
      setError(`Recording is too short — please record at least ${MIN_RECORDING_SEC} seconds.`);
      return;
    }
    setMode("uploading");
    setError(null);
    try {
      const ref = SCRIPTS[scriptLang].sentences.join(" ");
      await enrollVoice({
        audio: audioBlob,
        audioFilename: `enrollment.webm`,
        referenceText: ref,
        language: scriptLang,
        durationSec: elapsedSec,
        consent: true,
      });
      setMode("done");
      await reloadStatus();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setMode("review");
    }
  }, [audioBlob, consent, elapsedSec, reloadStatus, scriptLang]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm(t("Delete your voice profile? Translated voice messages will stop being available in your voice."))) {
      return;
    }
    setError(null);
    try {
      await deleteVoiceProfile();
      await reloadStatus();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [reloadStatus, t]);

  const resetRecording = useCallback(() => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setElapsedSec(0);
    setMode("idle");
    setError(null);
  }, [audioUrl]);

  if (disabled) return null;

  return (
    <section className={`voice-enroll ${className || ""}`}>
      <header className="voice-enroll__header">
        <span className="voice-enroll__icon" aria-hidden>🎙</span>
        <h3 className="voice-enroll__title">{t("Voice Clone")}</h3>
        {status?.enrolled && <span className="voice-enroll__badge voice-enroll__badge--ok">{t("Enrolled")}</span>}
      </header>

      <p className="voice-enroll__intro">
        {t("Record a short reference clip so the messenger can translate your voice messages into other languages while keeping your voice. Your audio stays on your team's servers — never sent to an external service.")}
      </p>

      {statusLoading && <div className="voice-enroll__hint">{t("Loading status…")}</div>}

      {!statusLoading && status?.enrolled && mode !== "uploading" && (
        <div className="voice-enroll__current">
          <div className="voice-enroll__current-row">
            <span className="voice-enroll__label">{t("Status")}</span>
            <span>{t("Active ({{language}}, {{duration}})", {
              language: status.language || "—",
              duration: status.durationSec ? `${status.durationSec.toFixed(1)}s` : "?",
            })}</span>
          </div>
          {status.consentAt && (
            <div className="voice-enroll__current-row">
              <span className="voice-enroll__label">{t("Consent at")}</span>
              <span>{new Date(status.consentAt).toLocaleString(getBcp47Locale())}</span>
            </div>
          )}
          <div className="voice-enroll__actions">
            <button className="voice-enroll__btn" onClick={resetRecording}>{t("Re-enroll")}</button>
            <button className="voice-enroll__btn voice-enroll__btn--danger" onClick={handleDelete}>
              {t("Delete profile")}
            </button>
          </div>
        </div>
      )}

      {(mode === "idle" || mode === "recording" || mode === "review") && (
        <div className="voice-enroll__recorder">
          <div className="voice-enroll__lang-row">
            <span className="voice-enroll__label">{t("Script language")}</span>
            <div className="voice-enroll__lang-tabs">
              {Object.entries(SCRIPTS).map(([code, s]) => (
                <button
                  key={code}
                  type="button"
                  className={`voice-enroll__lang ${scriptLang === code ? "voice-enroll__lang--active" : ""}`}
                  onClick={() => setScriptLang(code as keyof typeof SCRIPTS)}
                  disabled={mode !== "idle"}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="voice-enroll__script">
            <span className="voice-enroll__label">{t("Read these two sentences out loud (~7-10 seconds total)")}</span>
            <ol>
              {SCRIPTS[scriptLang].sentences.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
          </div>

          <div className="voice-enroll__controls">
            {mode === "idle" && (
              <button type="button" className="voice-enroll__btn voice-enroll__btn--primary" onClick={startRecording}>
                ● {t("Start recording")}
              </button>
            )}
            {mode === "recording" && (
              <>
                <span className="voice-enroll__timer">
                  {t("Recording {{elapsed}}s / {{max}}s", { elapsed: elapsedSec.toFixed(1), max: MAX_RECORDING_SEC })}
                </span>
                <button type="button" className="voice-enroll__btn voice-enroll__btn--danger" onClick={stopRecording}>
                  ■ {t("Stop")}
                </button>
              </>
            )}
            {mode === "review" && audioUrl && (
              <>
                <audio src={audioUrl} controls className="voice-enroll__player" />
                <span className="voice-enroll__timer">{t("{{seconds}}s recorded", { seconds: elapsedSec.toFixed(1) })}</span>
                <button type="button" className="voice-enroll__btn" onClick={resetRecording}>
                  ↺ {t("Re-record")}
                </button>
              </>
            )}
          </div>

          {mode === "review" && (
            <>
              <label className="voice-enroll__consent">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                <span>
                  {t("I confirm this is my own voice and I consent to its use for translating my voice messages on this server. I can delete the profile at any time.")}
                </span>
              </label>
              <div className="voice-enroll__actions">
                <button
                  type="button"
                  className="voice-enroll__btn voice-enroll__btn--primary"
                  onClick={handleSubmit}
                  disabled={!consent}
                >
                  {t("Save voice profile")}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {mode === "uploading" && (
        <div className="voice-enroll__hint">{t("Uploading and processing…")}</div>
      )}

      {mode === "done" && (
        <div className="voice-enroll__hint voice-enroll__hint--ok">
          {t("✓ Voice profile saved. Open any of your voice messages in chat to test the translation toggle.")}
        </div>
      )}

      {error && <div className="voice-enroll__error">{error}</div>}
    </section>
  );
};

export default VoiceEnrollment;
